import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import http from 'services/http';
import { useGetUserPermission } from 'hook/permissions';

/**
 * Seção de gerenciamento de permissões Spatie de um usuário.
 * Só renderiza para quem tem a permissão 'Gerenciar permissões'.
 *
 * Comportamento:
 * - Permissões do role: marcadas e editáveis. Ao desmarcar alguma, o role é
 *   removido e todas as permissões passam a ser gerenciadas individualmente.
 * - Permissões extras (diretas): marcáveis livremente.
 * - "Restaurar padrões": remove diretas + restaura o role original do nível. Só
 *   aparece para nível que TEM role padrão — o subadmin não tem (nasce sem role,
 *   só com permissões diretas), e restaurar deixaria o usuário sem permissão
 *   nenhuma, tomando 401 e sendo deslogado em qualquer menu. O backend também
 *   recusa (ResetaPermissoesUsuarioService::temPadraoDeNivel).
 */
// Permissões que a tela oferece ao configurar um subadmin.
//
// REGRA: toda permissão de Criar/Alterar listada aqui precisa vir acompanhada do
// 'Listar'/'Ver' do mesmo domínio. Quem checa a permissão é o endpoint, e as telas
// carregam listas assim que abrem — Caixa chama /lancamentos, Venda e Descarga
// chamam /loterias e /usuarios. Sem o 'Listar', o subadmin recebia o menu, a tela
// tomava 401 e a sessão caía ("ajusto o sub admin, salvo e desloga no outro menu").
export const PERMISSOES_SUBADMIN = [
    'Listar dashboard',
    // Usuários — /usuarios é chamado pelos filtros de Caixa, Venda e Prestar Contas.
    'Listar cambista', 'Ver cambista', 'Criar cambista', 'Alterar cambista',
    'Listar gerente', 'Ver gerente', 'Criar gerente', 'Alterar gerente',
    'Alterar saldo',
    // Caixa — a tela carrega /lancamentos na abertura.
    'Listar caixa', 'Listar lançamento', 'Criar lançamento', 'Remover lançamento',
    'Listar aposta jb', 'Alterar aposta jb',
    'Listar prestação cambista', 'Listar resultado jb', 'Listar venda',
    // Extrações — /loterias alimenta os filtros de Venda, Bilhetes e Descarga.
    'Listar loteria', 'Ver loteria', 'Criar loteria', 'Alterar loteria',
    'Listar modalidade', 'Alterar modalidade',
    'Listar cotações SENA',
    'Listar seninha', 'Criar modalidades concurso',
    'Listar cotação sena brasil', 'Alterar cotação sena brasil',
    'Listar bolão', 'Ver bolão', 'Criar bolão', 'Alterar bolão',
    'Listar rifa', 'Criar rifa', 'Alterar rifa',
    'Listar bilhetinho', 'Criar bilhetinho', 'Alterar bilhetinho',
    'Listar bolão de dezenas', 'Criar bolão de dezenas', 'Alterar bolão de dezenas',
    'Listar sorteio', 'Criar sorteio', 'Remover sorteio',
    'Listar relatório',
    'Listar regulamento', 'Alterar regulamento',
    'Listar descarga', 'Listar modalidade descarga',
    // Rota de fallback do login quando nenhuma outra tela está liberada.
    'Alterar senha',
];

const PERMISSAO_LABELS = {
    'Listar dashboard':             'Acesso a dashboard',
    'Listar cambista':              'Acessar cambistas',
    'Ver cambista':                 'Abrir cambista',
    'Listar gerente':               'Acessar gerentes',
    'Ver gerente':                  'Abrir gerente',
    'Listar lançamento':            'Acessar lançamentos',
    'Listar loteria':               'Acessar extrações',
    'Ver loteria':                  'Abrir extração',
    'Listar modalidade':            'Acessar modalidades',
    'Listar sorteio':               'Acessar sorteios',
    'Listar regulamento':           'Acessar regulamento',
    'Listar bolão':                 'Acessar bolão',
    'Ver bolão':                    'Abrir bolão',
    'Listar rifa':                  'Acessar rifa',
    'Listar bilhetinho':            'Acessar 2pra500',
    'Listar bolão de dezenas':      'Acessar bolão de dezenas',
    'Alterar senha':                'Trocar a própria senha',
    'Criar cambista':               'Criar cambistas',
    'Alterar cambista':             'Editar cambistas',
    'Criar gerente':                'Criar gerente',
    'Alterar gerente':              'Editar gerentes',
    'Alterar saldo':                'Alterar saldo',
    'Listar caixa':                 'Acessar caixa',
    'Listar aposta jb':             'Acessar bilhetes',
    'Alterar aposta jb':            'Editar bilhetes',
    'Criar lançamento':             'Adicionar lançamento',
    'Remover lançamento':           'Excluir lançamento',
    'Listar prestação cambista':    'Prestar contas',
    'Listar resultado jb':          'Adicionar resultado',
    'Listar venda':                 'Acessar vendas',
    'Criar loteria':                'Criar extração',
    'Alterar loteria':              'Editar extração',
    'Alterar modalidade':           'Editar modalidades',
    'Listar cotações SENA':         'Editar cotação',
    'Listar seninha':               'Editar seninha',
    'Criar modalidades concurso':   'Editar modalidades seninha',
    'Listar cotação sena brasil':   'Editar sena brasil',
    'Alterar cotação sena brasil':  'Editar modalidades sena brasil',
    'Criar bolão':                  'Criar bolão',
    'Alterar bolão':                'Editar bolão',
    'Criar rifa':                   'Criar rifa',
    'Alterar rifa':                 'Editar Rifa',
    'Criar bilhetinho':             'Criar 2pra500',
    'Alterar bilhetinho':           'Editar 2pra500',
    'Criar bolão de dezenas':       'Criar bolão de dezenas',
    'Alterar bolão de dezenas':     'Editar bolão de dezenas',
    'Criar sorteio':                'Criar sorteio',
    'Remover sorteio':              'Cancelar sorteio',
    'Listar relatório':             'Acesso a relatórios',
    'Alterar regulamento':          'Editar regulamento',
    'Listar descarga':              'Ver descarga',
    'Listar modalidade descarga':   'Editar descarga',
};

export default function SecaoPermissoes({ usuarioId, filtrarPor, permiteRestaurarPadrao = true }) {
    const permissoesLogado = useGetUserPermission();
    const podeGerenciar = Array.isArray(permissoesLogado) &&
        permissoesLogado.includes('Gerenciar permissões');
    const isAdmin = localStorage.getItem('nivel') === 'admin';

    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando]     = useState(false);
    const [dados, setDados]           = useState(null);
    // Conjunto completo de permissões que o usuário TERÁ após salvar
    const [selecionadas, setSelecionadas] = useState([]);

    const carregar = useCallback(async () => {
        if (!usuarioId || !podeGerenciar) return;
        setCarregando(true);
        try {
            const { data } = await http.get(`/usuarios/${usuarioId}/permissoes`);
            const d = data.data;
            setDados(d);
            // Inicializa com todas as permissões que o usuário já tem (role + diretas)
            const todas = [...new Set([
                ...(d.permissoes_role    ?? []),
                ...(d.permissoes_diretas ?? []),
            ])];
            setSelecionadas(todas);
        } catch {
            toast.error('Erro ao carregar permissões do usuário.');
        } finally {
            setCarregando(false);
        }
    }, [usuarioId, podeGerenciar]);

    useEffect(() => { carregar(); }, [carregar]);

    const toggle = (nome) => {
        setSelecionadas((prev) =>
            prev.includes(nome)
                ? prev.filter((p) => p !== nome)
                : [...prev, nome]
        );
    };

    // Verifica se alguma permissão do role foi desmarcada pelo gestor
    const rolePerms       = dados?.permissoes_role ?? [];
    const removeuDoRole   = rolePerms.some((p) => !selecionadas.includes(p));
    const modoCustomizado = dados?.modo_customizado ?? false;

    const salvar = async () => {
        if (removeuDoRole && !window.confirm(
            'Ao remover uma permissão do nível, o role será desvinculado e todas as ' +
            'permissões passarão a ser gerenciadas individualmente. Continuar?'
        )) return;

        setSalvando(true);
        try {
            await http.post(`/usuarios/${usuarioId}/permissoes/sync`, {
                permissoes: selecionadas,
            });
            toast.success('Permissões salvas com sucesso!');
            carregar();
        } catch (e) {
            toast.error(e?.response?.data?.message ?? 'Erro ao salvar permissões.');
        } finally {
            setSalvando(false);
        }
    };

    const restaurarPadrao = async () => {
        if (!window.confirm('Restaurar as permissões padrão do nível? Permissões extras serão removidas e o role será restaurado.')) return;
        setSalvando(true);
        try {
            await http.post(`/usuarios/${usuarioId}/permissoes/reset`);
            toast.success('Permissões redefinidas para o padrão do nível.');
            carregar();
        } catch (e) {
            toast.error(e?.response?.data?.message ?? 'Erro ao redefinir permissões.');
        } finally {
            setSalvando(false);
        }
    };

    if (!podeGerenciar) return null;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h4 style={styles.titulo}>Permissões</h4>
                {dados?.role && !modoCustomizado && (
                    <span style={styles.badge}>Role: {dados.role}</span>
                )}
                {(modoCustomizado || removeuDoRole) && (
                    <span style={styles.badgeAviso}>
                        ⚠ Modo customizado — sem role vinculado após salvar
                    </span>
                )}
            </div>

            <div style={styles.legenda}>
                <span style={styles.legItem}>
                    <span style={{ ...styles.dot, background: '#6c757d' }} />
                    Do role (editável — desmarcar remove o role)
                </span>
                <span style={styles.legItem}>
                    <span style={{ ...styles.dot, background: '#28a745' }} />
                    Concedida individualmente
                </span>
            </div>

            {carregando && <p style={{ color: '#888' }}>Carregando permissões…</p>}

            {!carregando && dados && (
                <>
                    {Object.entries(dados.todas_permissoes ?? {}).map(([grupo, lista]) => {
                        const listaFiltrada = filtrarPor
                            ? (lista ?? []).filter(p => filtrarPor.includes(p))
                            : (lista ?? []);
                        if (!listaFiltrada.length) return null;
                        return (
                            <div key={grupo} style={styles.grupo}>
                                <div style={styles.grupoTitulo}>{grupo}</div>
                                <div style={styles.grid}>
                                    {listaFiltrada.map((nome) => {
                                        // 'Gerenciar permissões' só visível para admin
                                        if (nome === 'Gerenciar permissões' && !isAdmin) return null;

                                        const isRole   = rolePerms.includes(nome);
                                        const checked  = selecionadas.includes(nome);
                                        // Verde: marcada mas NÃO é do role (extra direta)
                                        // Vermelho: era do role mas foi desmarcada
                                        // Cinza: do role e ainda marcada
                                        // Normal: não é do role e não está marcada
                                        let cor = '#333';
                                        if (isRole && checked)   cor = '#6c757d';
                                        if (!isRole && checked)  cor = '#28a745';
                                        if (isRole && !checked)  cor = '#dc3545';

                                        return (
                                            <label key={nome} style={styles.label}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggle(nome)}
                                                    style={{ marginRight: 6 }}
                                                />
                                                <span style={{ color: cor }}>{PERMISSAO_LABELS[nome] ?? nome}</span>
                                                {isRole && !checked && (
                                                    <span style={{ ...styles.tag, color: '#dc3545' }}> (removida)</span>
                                                )}
                                                {isRole && checked && (
                                                    <span style={styles.tag}> (role)</span>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    <div style={styles.acoes}>
                        <button
                            className="btn btn-success"
                            onClick={salvar}
                            disabled={salvando}
                            style={{ marginRight: 10 }}
                        >
                            {salvando ? 'Salvando…' : 'Salvar alterações'}
                        </button>
                        {permiteRestaurarPadrao && (
                            <button
                                className="btn btn-outline-secondary"
                                onClick={restaurarPadrao}
                                disabled={salvando}
                            >
                                Restaurar padrões do nível
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

const styles = {
    container: {
        border: '1px solid #dee2e6',
        borderRadius: 6,
        padding: '16px 20px',
        margin: '24px 0',
        background: '#fafafa',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 10,
        flexWrap: 'wrap',
    },
    titulo: { margin: 0, fontSize: 16, fontWeight: 600 },
    badge: {
        background: '#e9ecef',
        borderRadius: 4,
        padding: '2px 8px',
        fontSize: 12,
        color: '#495057',
    },
    badgeAviso: {
        background: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: 4,
        padding: '2px 8px',
        fontSize: 12,
        color: '#856404',
    },
    legenda: {
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        marginBottom: 16,
        fontSize: 12,
        color: '#555',
    },
    legItem: { display: 'flex', alignItems: 'center', gap: 4 },
    dot: {
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
    },
    grupo: { marginBottom: 16 },
    grupoTitulo: {
        fontWeight: 600,
        fontSize: 13,
        color: '#495057',
        borderBottom: '1px solid #dee2e6',
        paddingBottom: 4,
        marginBottom: 8,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '6px 16px',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        fontSize: 13,
        userSelect: 'none',
    },
    tag: { fontSize: 10, color: '#6c757d', marginLeft: 4 },
    acoes: {
        marginTop: 20,
        paddingTop: 16,
        borderTop: '1px solid #dee2e6',
    },
};
